import {
  IconButton,
  TextField,
  TextFieldProps,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useLocation } from "react-router-dom";
import debounce from 'lodash/debounce';
import { translations } from '../translations/common';

const FILTER_PARAM = "tableFilter";
const DEBOUNCE_DELAY = 300;

interface TableSearchProps extends Omit<TextFieldProps, 'onChange'> {
  onSearch?: (value: string) => void;
  debounceMs?: number;
}

const SearchTextField = styled(TextField)({
  margin: 0,
  width: '200px',
  "& .MuiInputBase-root": {
    height: '36px',
    padding: '0 8px',
  },
  "& .MuiInputBase-input": {
    padding: '8px 0',
    fontSize: '0.875rem',
  },
});

export const TableSearch: React.FC<TableSearchProps> = ({
  onSearch,
  debounceMs = DEBOUNCE_DELAY,
  placeholder = translations.search.placeholder,
  ...props
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialFilter = useMemo(() => 
    new URLSearchParams(location.search).get(FILTER_PARAM) || "", 
    [location.search]
  );

  const [filter, setFilter] = React.useState<string>(initialFilter);

  const updateQueryParams = useCallback((searchValue: string) => {
    const params = new URLSearchParams(location.search);
    params.delete(FILTER_PARAM);
    params.delete("page");
    
    if (searchValue) {
      params.append(FILTER_PARAM, searchValue);
    }
    
    navigate({ search: params.toString() }, { replace: true });
    onSearch?.(searchValue);
  }, [navigate,location.search, onSearch]);

  const debouncedSearch = useMemo(
    () => debounce(updateQueryParams, debounceMs),
    [updateQueryParams, debounceMs]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFilter(newValue);
    debouncedSearch(newValue);
  };

  const handleClear = () => {
    setFilter("");
    updateQueryParams("");
  };

  return (
    <SearchTextField
      size="small"
      type="text"
      name="tableFilter"
      placeholder={placeholder}
      value={filter}
      onChange={handleFilterChange}
      {...props}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: filter && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              aria-label={translations.search.clearLabel}
              onClick={handleClear}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        ...props.InputProps,
      }}
    />
  );
};
